import { useCallback, useMemo } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import * as Sentry from '@sentry/react';
import { Flex, Text, Button, Icon, IconButton, Dropdown, Card, iconWeight, zIndex } from '@nula/core-ui';
import { sessionOpenTab } from '@/session';
import { useUIStore } from '@/stores/use-ui-store';
import { useConversationList } from '../hooks/use-conversation-list';
import { useChatSuggestions } from '../hooks/use-chat-suggestions';
import { updateConversation, deleteConversation } from '../api/conversations';
import { relativeTime } from '../utils/relative-time';
import { MarkdownRenderer } from './messages/markdown-renderer';
import { NulaOrb } from './nula-orb';
import styles from './assistant-home.module.css';

const root = cva(styles.root);

/** Most-recent conversations surfaced on the home state before any cap. */
const MAX_RECENTS = 5;

interface RecentItemProps {
  row: ReturnType<typeof useConversationList>[number];
  onPin: (id: string, pinned: boolean) => void;
  onDelete: (id: string) => void;
}

function RecentItem({ row, onPin, onDelete }: RecentItemProps) {
  const menuItems = useMemo(
    () => [
      {
        key: 'pin',
        label: row.isPinned ? 'Unpin' : 'Pin',
        icon: <Icon name="push_pin" size={14} weight={row.isPinned ? iconWeight.regular : iconWeight.light} />,
        onClick: () => onPin(row.id, !row.isPinned),
      },
      { key: 'div', label: '', type: 'divider' as const },
      {
        key: 'delete',
        label: 'Delete',
        icon: <Icon name="delete" size={14} weight={iconWeight.light} />,
        danger: true,
        onClick: () => onDelete(row.id),
      },
    ],
    [row.id, row.isPinned, onPin, onDelete],
  );

  return (
    <Flex
      vertical
      gap={0}
      role="button"
      tabIndex={0}
      aria-label={row.title}
      className={styles.recentItem}
      onClick={() => sessionOpenTab(row.id, 'conversation')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          sessionOpenTab(row.id, 'conversation');
        }
      }}
    >
      <Flex align="center" justify="space-between" gap="sm" className={styles.recentTitleRow}>
        <Flex align="center" gap="xs" className={styles.recentTitleGroup}>
          {row.isPinned && (
            <Icon
              name="push_pin"
              size={12}
              weight={iconWeight.regular}
              className={styles.recentPinIcon}
              data-testid="pinned-indicator"
            />
          )}
          <Text ellipsis className={styles.recentTitle}>{row.title}</Text>
        </Flex>
        <Flex align="center" gap="xs" className={styles.recentMeta}>
          <Text className={styles.recentTime}>{relativeTime(row.latestActivityAt)}</Text>
          <Flex className={styles.recentActions} onClick={(e) => e.stopPropagation()}>
            <Dropdown
              trigger={['click']}
              menu={{ items: menuItems }}
              placement="bottom-end"
              // This home state renders inside the floating assistant overlay,
              // whose panel sits at zIndex.modal and creates a stacking context
              // via backdrop-filter. The default dropdown z (200) put the menu
              // behind it — the "..." looked dead. Same lift conversation-list
              // -item applies against the right-side drawer.
              menuStyle={{ '--dropdown-z': zIndex.popover } as React.CSSProperties}
            >
              <IconButton size="sm" aria-label="Conversation actions">
                <Icon name="more_horiz" size={14} weight={iconWeight.light} />
              </IconButton>
            </Dropdown>
          </Flex>
        </Flex>
      </Flex>
      {row.snippet ? (
        // Rich markdown preview (same renderer as messages) but visually
        // inert — pointer-events:none in CSS so a click opens the
        // conversation rather than following a link inside the snippet.
        // Clamped to the first line.
        <div className={styles.recentSnippet} aria-hidden="true">
          <MarkdownRenderer>{row.snippet}</MarkdownRenderer>
        </div>
      ) : null}
    </Flex>
  );
}

interface AssistantHomeProps {
  /** Fired when a starter prompt is clicked — fills the input (handled by the surface). */
  onSuggestionClick?: (text: string) => void;
  className?: string;
}

/**
 * The assistant home / empty state. Shown by a surface (overlay, full tab,
 * sidebar) when the chat has no messages yet. Surfaces the user's recent
 * conversations (click to resume in a tab) plus a quiet "Try asking" list of
 * starter prompts. With no conversations, only the prompts show (new-user
 * onboarding); the prompts always render so there's a way in.
 */
export function AssistantHome({ onSuggestionClick, className }: AssistantHomeProps) {
  const allConversations = useConversationList();
  const suggestions = useChatSuggestions();
  const recents = allConversations.slice(0, MAX_RECENTS);
  const hasRecents = recents.length > 0;
  const hasMore = allConversations.length > MAX_RECENTS;

  const handlePin = useCallback((id: string, pinned: boolean) => {
    updateConversation(id, { isPinned: pinned }).catch((err) => {
      Sentry.captureException(err, { extra: { action: 'pin', id } });
    });
  }, []);

  const handleDelete = useCallback((id: string) => {
    deleteConversation(id).catch((err) => {
      Sentry.captureException(err, { extra: { action: 'delete', id } });
    });
  }, []);

  return (
    <Flex
      vertical
      align={hasRecents ? 'stretch' : 'center'}
      justify={hasRecents ? 'start' : 'center'}
      gap="lg"
      className={clsx(root(), !hasRecents && styles.empty, className)}
    >
      <Flex vertical align="center" gap="md" className={styles.header}>
        <Flex align="center" justify="center" className={styles.glyph}>
          <NulaOrb size={48} />
        </Flex>
        <Text className={styles.title}>What&apos;s on your mind?</Text>
      </Flex>

      {hasRecents && (
        <Flex vertical gap="sm" className={styles.recents}>
          <Text className={styles.sectionLabel}>Continue a conversation</Text>
          {recents.map((row) => (
            <RecentItem
              key={row.id}
              row={row}
              onPin={handlePin}
              onDelete={handleDelete}
            />
          ))}
          {hasMore && (
            <Button
              variant="text"
              size="sm"
              className={styles.viewAll}
              onClick={() => useUIStore.getState().setAssistantHistoryOpen(true)}
            >
              View all
            </Button>
          )}
        </Flex>
      )}

      <Flex vertical gap="sm" className={styles.prompts}>
        <Text className={styles.sectionLabel}>Try asking</Text>
        <div className={styles.suggestionsGrid}>
          {suggestions.map((s) => (
            <Card
              key={s.key}
              hoverable
              className={styles.suggestionCard}
              onClick={() => onSuggestionClick?.(s.prompt)}
              style={{ '--suggestion-color': s.color } as React.CSSProperties}
            >
              <div className={styles.suggestionIconTile}>
                <Icon name={s.icon} size={17} weight={iconWeight.light} />
              </div>
              <Flex vertical gap={0}>
                <Text className={styles.suggestionLabel}>{s.title}</Text>
                <Text className={styles.suggestionSub}>{s.subtitle}</Text>
              </Flex>
            </Card>
          ))}
        </div>
      </Flex>
    </Flex>
  );
}
